import { ReactElement } from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Button, Stack, Typography } from '@mui/material'
import { IfcPropertiesView } from '@editor/components/IFCElementForm/components'
import { IFC_OTHER_TYPES } from '@editor/components/StoreyControls/IFCStoreyControls/IFCOtherTypesFilter'
import { CorneredSection } from '@ui2/structure'
import { useControlStore } from '@editorStores'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import IFCSelectionInfo from '../IFCSelectionInfo'

/**
 * "Orientieren im Modell" guided-import step: a short orientation hint plus a toggle hiding/showing
 * all "Sonstiges" IFC element types — the same `glbTypeVisibility` state driven by the storey
 * controls' IFC column, so both stay in sync. IFC elements can be selected in the scene (click or
 * marquee) to inspect their properties in the "Eigenschaften" section.
 */
const IFCOrientationPanel = (): ReactElement => {
  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const setGlbTypeVisibility = useControlStore(state => state.setGlbTypeVisibility)

  const { selectedIfcElementGuids, setSelectedIfcElementGuids, metadata } = useIFCElementSelection()

  const anyOtherTypeVisible = IFC_OTHER_TYPES.some(type => glbTypeVisibility[type] !== false)

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <IFCSelectionInfo showWhenEmpty />
      </CorneredSection>

      <CorneredSection>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Orientieren Sie sich im Modell. In der Regel können Sie alle sonstigen Elemente
            ausblenden. Wenn diese jedoch zwingend erforderlich sind, können Sie sie eingeblendet
            lassen. Sie können sie auch jederzeit über die Filter in der Mitte oben ein- und
            ausblenden.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
            startIcon={anyOtherTypeVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() =>
              IFC_OTHER_TYPES.forEach(type => setGlbTypeVisibility(type, !anyOtherTypeVisible))
            }
            data-cy="btn-guided-ifc-toggle-other-types"
          >
            {anyOtherTypeVisible ? 'Sonstige Elemente ausblenden' : 'Sonstige Elemente einblenden'}
          </Button>
        </Stack>
      </CorneredSection>

      {selectedIfcElementGuids.length > 0 && (
        <CorneredSection title="Eigenschaften" collapsible defaultOpen>
          <IfcPropertiesView
            guids={selectedIfcElementGuids}
            metadata={metadata}
            onSelectGuids={setSelectedIfcElementGuids}
          />
        </CorneredSection>
      )}
    </Stack>
  )
}

export default IFCOrientationPanel
